import { isPlatformBrowser } from '@angular/common';
import { Injectable, Inject, inject, PLATFORM_ID, Optional } from '@angular/core';
import { WINDOW } from 'core/utils/window.token';
import { USER_AGENT } from 'core/static/user-agent.token';


@Injectable({ providedIn: 'root' })
export class WindowService {
    static isBrowser = true;
    private ssrUserAgent?: string;

    get localStorage(): Storage | null {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        return window.localStorage;
    }

    private _platformId = inject(PLATFORM_ID);

    constructor(
        @Inject(WINDOW) private win: Window | null,
        @Optional() @Inject(USER_AGENT) private uaFromServer?: string,
    ) {
        WindowService.isBrowser = isPlatformBrowser(this._platformId);
        this.ssrUserAgent = this.extractUserAgent();
    }

    // ---- UUID ----
    static uuid(): string {
        // Fallback: simple UUID v4
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /** Safe getter for native window */
    get native(): Window | null {
        return this.win;
    }

    // ---- Styles ----
    getComputedStyle(el: Element): CSSStyleDeclaration | undefined {
        return this.win ? this.win.getComputedStyle(el) : undefined;
    }

    getBoundingClientRect(el: Element | null): DOMRect {
        if (el && typeof el.getBoundingClientRect === 'function') {
            return el.getBoundingClientRect();
        }
        // Return dummy DOMRect
        return {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            toJSON: () => ({})
        } as DOMRect;
    }

    matchMedia(query: string): boolean {
        if (!this.win || typeof this.win.matchMedia !== 'function') return false; // SSR fallback
        return this.win.matchMedia(query).matches;
    }

    // ---- Navigator ----
    get userAgent(): string | undefined {
        return this.win?.navigator?.userAgent ?? this.ssrUserAgent ?? undefined;
    }

    get onLine(): boolean {
        return this.win?.navigator?.onLine ?? true;
    }

    // ---- Layout ----
    get innerWidth(): number {
        return this.win?.innerWidth ?? this.getSsrFallbackWidth();
    }

    get innerHeight(): number {
        return this.win?.innerHeight ?? 1080;
    }

    get outerHeight(): number {
        return this.win?.outerHeight ?? 1080;
    }

    // ---- URL / Blob ----
    createObjectURL(obj: Blob | MediaSource): string | undefined {
        if (!this.win) return undefined;
        return URL.createObjectURL(obj);
    }

    revokeObjectURL(url: string): void {
        if (!this.win) return;
        URL.revokeObjectURL(url);
    }

    // ---- Scroll ----
    get scrollY(): number | null {
        return this.win?.scrollY ?? null;
    }

    scrollTo(x: number, y: number, behavior: "auto" | "instant" | "smooth" = 'instant'): void {
        this.win?.scrollTo({top: y, left: x, behavior});
    }

    scrollX(x: number, behavior: "auto" | "instant" | "smooth" = 'instant'): void {
        this.win?.scrollTo({left: x, behavior});
    }

    // Optional helpers for convenience
    setItem(key: string, value: string): void {
        try {
            this.localStorage?.setItem(key, value);
        } catch {
            // fail silently in SSR or storage error
        }
    }

    getItem(key: string): string | null {
        return this.localStorage?.getItem(key) ?? null;
    }

    removeItem(key: string): void {
        this.localStorage?.removeItem(key);
    }

    clear(): void {
        this.localStorage?.clear();
    }

    requestAnimationFrame(callback: FrameRequestCallback): number {
        if (this.win?.requestAnimationFrame) {
        return this.win.requestAnimationFrame(callback);
        }
        // SSR: run immediately with timestamp 0
        callback(0);
        return 0;
    }

    cancelAnimationFrame(id: number): void {
        if (this.win?.cancelAnimationFrame) {
        this.win.cancelAnimationFrame(id);
        }
    }

    private extractUserAgent(): string | undefined {
        if (this.uaFromServer) {
            return Array.isArray(this.uaFromServer) ? this.uaFromServer[0] : this.uaFromServer;
        }
        return undefined;
    }

    private getSsrFallbackWidth(): number {
        const ua = (this.ssrUserAgent || '').toLowerCase();
        if (!ua) return 1420;

        const isMobile = /iphone|android.*mobile|mobile|phone/i.test(ua);
        const isTablet = /ipad|tablet|silk|kindle|playbook|android(?!.*mobile)/i.test(ua);

        if (isMobile) return 475;
        if (isTablet) return 1024;
        return 1420;
    }
}
